// Words for the You page, derived from numbers only (never from a model).
// Each helper answers one child-simple question about a goal card:
// what's the goal, how far along am I, which way am I going, when do I get there.
import { formatShort } from '../twin/format';

// "6h 50m" · "7,900" · "96%" · "64"
export const bigText = (v, unit) => {
  if (v == null) return null;
  if (unit === 'min') return formatShort(v, unit);
  const n = formatShort(v, unit);
  return unit === '%' ? `${n}%` : n;
};

// The small words after the big number.
export const unitWords = (unit, metricKey = '') => {
  switch (unit) {
    case 'bpm': return 'beats a minute';
    case 'steps': return 'a day';
    case 'min': return /sleep/.test(metricKey) ? 'a night' : 'a day';
    case 'kg': return 'kilos';
    case 'lb': return 'pounds';
    case '/100': return 'out of 100';
    case '/10': return 'out of 10';
    case 'kcal': return 'calories a day';
    case 'g': return 'grams a day';
    case 'ml': return 'ml a day';
    case '%': return '';
    default: return unit;
  }
};

const fmt = (v, unit) => {
  if (v == null) return '';
  if (unit === 'min') return v < 60 ? `${Math.round(v)} minutes` : formatShort(v, unit);
  const n = formatShort(v, unit);
  return unit === '%' ? `${n}%` : n;
};

// "under 60" · "10,000 or more" · "7h – 9h"
export const goalText = (card) => {
  const { direction, ultimate_low: lo, ultimate_high: hi, unit } = card;
  if (direction === 'down' && hi != null) return `under ${fmt(hi, unit)}`;
  if (direction === 'up' && lo != null) return `${fmt(lo, unit)} or more`;
  if (lo != null && hi != null) return `${fmt(lo, unit)} – ${fmt(hi, unit)}`;
  if (lo != null) return `${fmt(lo, unit)} or more`;
  if (hi != null) return `under ${fmt(hi, unit)}`;
  return null;
};

// The band edge a value is walking toward. null = already inside.
export const goalEdge = (v, card) => {
  const { direction, ultimate_low: lo, ultimate_high: hi } = card;
  if (v == null) return null;
  if (direction === 'up') return lo != null && v < lo ? lo : null;
  if (direction === 'down') return hi != null && v > hi ? hi : null;
  if (lo != null && v < lo) return lo;
  if (hi != null && v > hi) return hi;
  return null;
};

export const inBand = (v, card) => v != null && goalEdge(v, card) === null &&
  (card.ultimate_low != null || card.ultimate_high != null);

// 0–100: how far from where you started to the goal edge. 100 once inside.
export const progressToGoal = (card) => {
  const { start_value: start, now } = card;
  if (now == null) return null;
  if (inBand(now, card)) return 100;
  if (start == null) return null;
  const dist = (v) => {
    const e = goalEdge(v, card);
    return e == null ? 0 : Math.abs(v - e);
  };
  const s = dist(start); const n = dist(now);
  if (s === 0) return 0;
  return Math.max(0, Math.min(100, Math.round(((s - n) / s) * 100)));
};

// Is the trend taking you toward the goal? 'good' | 'bad' | 'flat' | null
export const trendTone = (card, metric) => {
  if (!metric || !metric.enough_data || metric.trend == null) return null;
  if (metric.trend === 'flat') return 'flat';
  const rising = metric.trend === 'rising';
  if (card.direction === 'up') return rising ? 'good' : 'bad';
  if (card.direction === 'down') return rising ? 'bad' : 'good';
  // range: moving toward the band is good
  const edge = goalEdge(card.now, card);
  if (edge == null) return 'flat';
  return (edge > card.now) === rising ? 'good' : 'bad';
};

export const progressWords = (pct, tone, there) => {
  if (there) return 'there';
  if (tone === 'bad') return 'heading the wrong way';
  if (pct == null) return null;
  if (pct >= 67) return 'most of the way there';
  if (pct >= 40) return 'about halfway';
  if (pct >= 15) return 'a quarter of the way';
  return 'just started';
};

// Ring colour: green when it's going well, red when it's going wrong, else neutral.
export const ringTone = (pct, tone, there) => {
  if (there) return 'g';
  if (tone === 'bad') return 'r';
  if (pct != null && pct >= 67) return 'g';
  return null;
};

// Which way the big arrow points, from the trend first, else start → now.
export const arrowDir = (card, metric) => {
  if (metric?.enough_data && metric.trend && metric.trend !== 'flat') {
    return metric.trend === 'rising' ? 'up' : 'down';
  }
  if (card.start_value != null && card.now != null && card.start_value !== card.now) {
    return card.now > card.start_value ? 'up' : 'down';
  }
  return null;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const monthFromNow = (weeks) => {
  const d = new Date(); d.setDate(d.getDate() + Math.round(weeks * 7));
  return MONTHS[d.getMonth()];
};
const monthAgo = (weeks) => monthFromNow(-weeks);

// A change, in words: "Down 4" · "Up 20 minutes" · "Up 0.4%" · "Down 5 points"
const changeWords = (delta, unit) => {
  const dir = delta > 0 ? 'Up' : 'Down';
  const a = Math.abs(delta);
  if (unit === 'min') return `${dir} ${Math.round(a)} minute${Math.round(a) === 1 ? '' : 's'}`;
  if (unit === '%') return `${dir} ${formatShort(Math.round(a * 10) / 10, unit)}%`;
  if (unit === '/100' || unit === '/10') return `${dir} ${formatShort(Math.round(a), unit)} points`;
  const n = a >= 20 ? Math.round(a) : Math.round(a * 10) / 10;
  return `${dir} ${formatShort(n, unit)}`;
};

// The one sentence under the number.
export const sentenceFor = (card, metric) => {
  const { now, start_value: start, start_weeks_ago: weeks, unit } = card;
  if (now == null) return 'Nothing measured yet. Connect a device, or log it on the Log page.';
  const parts = [];
  if (start != null && weeks != null && Math.abs(now - start) > 0) {
    parts.push(`${changeWords(now - start, unit)} since ${monthAgo(weeks)}.`);
  }
  const there = inBand(now, card);
  const tone = trendTone(card, metric);
  if (there) {
    parts.push(tone === 'bad' ? 'You’re there, but it’s starting to slip.' : 'You’re there. Now it’s about holding it.');
    return parts.join(' ');
  }
  const edge = goalEdge(now, card);
  const slope = metric?.enough_data ? metric.slope_per_week : null;
  if (edge != null && slope != null && slope !== 0 && tone === 'good') {
    const w = (edge - now) / slope;
    if (w <= 0) parts.push('You’re about to get there.');
    else if (w <= 10) parts.push('If this keeps up, you get there in a few weeks.');
    else if (w <= 60) parts.push(`If this keeps up, you get there around ${monthFromNow(w)}.`);
    else if (w <= 120) parts.push('At this pace, about a year to go.');
    else parts.push('At this pace it would take years. A bigger change would help.');
  } else if (tone === 'bad') {
    parts.push('Right now it’s drifting away from the goal.');
  } else if (tone === 'flat') {
    parts.push('Holding steady. It needs a nudge to move.');
  } else if (metric && !metric.enough_data) {
    const have = metric.n_points || 0;
    parts.push(`Gathering data: ${have} of ${metric.min_points} days. The chart shows up after that.`);
  }
  return parts.join(' ') || 'Keep going.';
};
